import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { QrCode, Download, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Enhanced Vehicle Safety Enforcement, Phase D: the ConfirmPH vehicle QR
 * identity card. Independent of Enhanced Safety program state (a QR works
 * whether or not the business has ever activated the program) - shown to
 * anyone with "vehicles" permission, same gate as photos/damage maps.
 */
export default function VehicleQrPanel({ vehicleId }) {
  const [qr, setQr] = useState(null);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: q }, { data: img }] = await Promise.all([
        api.get(`/vehicles/${vehicleId}/qr`),
        api.get(`/vehicles/${vehicleId}/qr/image`),
      ]);
      setQr(q);
      setImage(img);
    } catch (_) {
      // tolerate: panel simply doesn't render its content below
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const rotate = async () => {
    setRotating(true);
    try {
      await api.post(`/vehicles/${vehicleId}/qr/rotate`, {});
      await load();
      setConfirmingRotate(false);
      toast.success("Vehicle code rotated: the old printed/scanned code no longer works.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRotating(false);
    }
  };

  if (loading) return null;
  if (!qr || !image) return null;

  const dataUrl = `data:${image.content_type};base64,${image.image_data}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="vehicle-qr-panel">
      <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
        <QrCode className="w-4 h-4 text-[#1B4332]" /> Vehicle identity code
      </h3>
      <p className="text-xs text-slate-500 mt-1 max-w-md">
        Print this on a sticker for the vehicle. Staff and renters can scan it (with any camera
        app) or enter it at pickup to confirm this is the exact ConfirmPH vehicle assigned to a
        booking. It proves identity only - it is not a safety or mechanical certification.
      </p>
      <div className="mt-4 flex flex-wrap items-start gap-4">
        <img src={dataUrl} alt="Vehicle QR code" className="w-32 h-32 border border-slate-200 rounded-lg"
             data-testid="vehicle-qr-image" />
        <div className="flex flex-col gap-2 text-sm">
          <div className="text-slate-500 text-xs">Generation {qr.generation}</div>
          <a href={dataUrl} download={`vehicle-${vehicleId}-qr.png`}
             data-testid="vehicle-qr-download"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 w-fit">
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          {!confirmingRotate ? (
            <button onClick={() => setConfirmingRotate(true)} data-testid="vehicle-qr-rotate-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 w-fit">
              <RefreshCcw className="w-3.5 h-3.5" /> Rotate code
            </button>
          ) : (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2 max-w-xs">
              <p className="text-xs text-amber-900">
                Old printed/scanned codes will stop working immediately. Use this only if the
                sticker was lost, copied, or the vehicle sticker was replaced.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmingRotate(false)} className="px-2.5 py-1 text-xs rounded-md hover:bg-amber-100">
                  Cancel
                </button>
                <button onClick={rotate} disabled={rotating} data-testid="vehicle-qr-rotate-confirm"
                        className="px-2.5 py-1 text-xs rounded-md bg-amber-600 text-white hover:opacity-90 disabled:opacity-50">
                  Rotate now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
